import React from "react";
import WelcomeCard from "../../components/WelcomeCard";
import { useSelector } from "react-redux";
import { useQuery } from "@tanstack/react-query";
import {
  fetchJobAutomationData,
  fetchJobAutomationPercentageData,
} from "../../services/adminService";
import SecondaryLoader from "../../components/SecondaryLoader/SecondaryLoader";
import AdminDashboardCard from "../../components/Admin/AdminDashboardCard";
import TotalUsersIcon from "../../images/svgs/TotalUsersIcon";
import KeyInsights from "../../components/keyInsights";
import { raceColors } from "../../constants/constants";
import BarChartComponent from "../../components/barChart/BarChart";

const JobAutomationReport = () => {
  const userId = useSelector((state) => state.auth.id);

  const {
    data: jobAutomationData,
    isLoading,
    error,
  } = useQuery({
    queryKey: ["jobAutomationData"],
    queryFn: fetchJobAutomationData,
    enabled: !!userId,
  });

  const low = jobAutomationData?.data?.Low?.totalUsers || 0;
  const moderate = jobAutomationData?.data?.Moderate?.totalUsers || 0;
  const high = jobAutomationData?.data?.High?.totalUsers || 0;
  const critical = jobAutomationData?.data?.Critical?.totalUsers || 0;

  const cardDetails = [
    {
      label: "Low",
      figure: low,
      icon: <TotalUsersIcon />,
    },
    {
      label: "Moderate",
      figure: moderate,
      icon: <TotalUsersIcon />,
    },
    {
      label: "High",
      figure: high,
      icon: <TotalUsersIcon />,
    },
    {
      label: "Critical",
      figure: critical,
      icon: <TotalUsersIcon />,
    },
  ];

  const getUsedRaceLabels = (data) => {
    const raceSet = new Set();

    Object.values(data || {}).forEach((races) => {
      Object.keys(races).forEach((race) => {
        raceSet.add(race);
      });
    });

    return Array.from(raceSet);
  };

  // 3. Transform data into chart format
  const transformStackedBarData = (data) => {
    if (!data) return [];

    const usedRaceLabels = getUsedRaceLabels(data).filter(
      (race) => race !== "totalUsers"
    );

    return Object.entries(data).map(([level, races]) => {
      const entry = { name: level };

      usedRaceLabels.forEach((race) => {
        entry[race] = races[race] || 0;
      });

      return entry;
    });
  };

  // 4. Generate bars dynamically for present races only
  const usedRaceLabels = getUsedRaceLabels(jobAutomationData?.data);

  const barChartData = transformStackedBarData(jobAutomationData?.data);

  const barChartBars = usedRaceLabels.map((race) => ({
    dataKey: race,
    color: raceColors[race] || "#D3D3D3", // fallback to grey
    label: race,
  }));

  const {
    data: automationRiskPercentageData,
    isLoading: automationRiskPercentageLoading,
    error: automationRiskPercentageError,
  } = useQuery({
    queryKey: ["automationRiskPercentageData"],
    queryFn: fetchJobAutomationPercentageData,
    enabled: !!userId,
  });

  console.log(
    "Automation Risk Percentage Data: ",
    automationRiskPercentageData
  );
  return (
    <div className="flex flex-col gap-6">
      <WelcomeCard title={"Job Automation Report"} />
      <>
        {" "}
        {isLoading ? (
          <SecondaryLoader />
        ) : (
          <>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-5">
              {cardDetails.map((card, index) => (
                <AdminDashboardCard
                  key={index}
                  label={card.label}
                  figure={card.figure}
                  description={card.description}
                  icon={card.icon}
                />
              ))}
            </div>
            {isLoading ? (
              <SecondaryLoader />
            ) : error ? (
              <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded-xl shadow-sm mb-4">
                <strong className="font-semibold">Oops!</strong> Something went
                wrong while loading race distribution data.
              </div>
            ) : !barChartData.length ? (
              <div className="bg-yellow-50 border border-yellow-300 text-yellow-700 px-4 py-3 rounded-xl shadow-sm mb-4">
                <strong className="font-semibold">Heads up!</strong> No quiz
                race distribution data available yet.
              </div>
            ) : (
              <div className="w-full">
                <BarChartComponent
                  title="Job Automation Breakdown by Race"
                  data={barChartData}
                  bars={barChartBars}
                  height={300}
                />
              </div>
            )}
            {automationRiskPercentageData && (
              <KeyInsights
                overviewDescription={`Among the total of ${automationRiskPercentageData.totalUsers} users, ${automationRiskPercentageData.high}% demonstrate high automation readiness, while ${automationRiskPercentageData.moderate}% show medium readiness and ${automationRiskPercentageData.low}% have low automation readiness.`}
                categoryDescription={
                  "The data reveals significant variations across different race groups. For example, in the IT occupation category, high automation readiness is more prevalent than in education, where low readiness is more common."
                }
                recommendationDescription={
                  "Consider targeted training initiatives for groups showing lower automation readiness scores, particularly in education and healthcare sectors where technological transformation may be accelerating."
                }
              />
            )}
          </>
        )}
      </>
    </div>
  );
};

export default JobAutomationReport;
